import { BIOMES, BIOME_ORDER } from "../content/biomes";
import type { BiomeId, DangerTier } from "../content/schemas";
import { hashSeed } from "../simulation/rng";

/** Assign biomes across the operational region from seed + chunk coords. */

/**
 * Biome regions are intentionally kilometres across.  Individual 96m chunks
 * still stream independently, but a player can read and learn a landscape
 * before crossing into a genuinely different one.
 */
export const BIOME_REGION_CHUNKS = 18;

export type BiomeBlend = {
  primary: BiomeId;
  secondary: BiomeId;
  /** 0 = primary region interior, 1 = shared macro-region border. */
  edge: number;
};

/**
 * Coherent Voronoi-like patches: a jittered macro-cell owns each chunk. This
 * removes the old one-or-two-chunk biome pinball while retaining deterministic
 * irregular borders and an unbounded world.
 */
export function biomeAt(seed: number, cx: number, cy: number): BiomeId {
  return biomeBlendAt(seed, cx + 0.5, cy + 0.5).primary;
}

/**
 * Continuous macro-region blend for terrain shape and palette. Gameplay can
 * retain a single owning biome while the landscape has a broad, natural
 * ecotone instead of a 96m tile seam.
 */
export function biomeBlendAt(seed: number, chunkX: number, chunkY: number): BiomeBlend {
  const rx = Math.floor(chunkX / BIOME_REGION_CHUNKS);
  const ry = Math.floor(chunkY / BIOME_REGION_CHUNKS);
  let first = { x: rx, y: ry, d2: Number.POSITIVE_INFINITY };
  let second = { x: rx, y: ry, d2: Number.POSITIVE_INFINITY };
  const localX = chunkX / BIOME_REGION_CHUNKS;
  const localY = chunkY / BIOME_REGION_CHUNKS;
  for (let oy = -1; oy <= 1; oy += 1) {
    for (let ox = -1; ox <= 1; ox += 1) {
      const gx = rx + ox;
      const gy = ry + oy;
      const centerX = gx + 0.22 + (hashSeed(seed + 17, gx * 1031 + gy * 1999) / 4294967296) * 0.56;
      const centerY = gy + 0.22 + (hashSeed(seed + 43, gx * 3253 + gy * 7877) / 4294967296) * 0.56;
      const d2 = (localX - centerX) ** 2 + (localY - centerY) ** 2;
      if (d2 < first.d2) {
        second = first;
        first = { x: gx, y: gy, d2 };
      } else if (d2 < second.d2) {
        second = { x: gx, y: gy, d2 };
      }
    }
  }
  const primary =
    BIOME_ORDER[hashSeed(seed, first.x * 374761393 + first.y * 668265263) % BIOME_ORDER.length]!;
  const secondary =
    BIOME_ORDER[hashSeed(seed, second.x * 374761393 + second.y * 668265263) % BIOME_ORDER.length]!;
  const distanceGap = Math.sqrt(second.d2) - Math.sqrt(first.d2);
  const edge = 1 - smoothstep(0.045, 0.19, distanceGap);
  return { primary, secondary, edge: primary === secondary ? 0 : edge };
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

export function biomeBlendLabel(seed: number, x: number, y: number, chunkSize: number): BiomeId {
  const cx = Math.floor(x / chunkSize);
  const cy = Math.floor(y / chunkSize);
  return biomeAt(seed, cx, cy);
}

/** Combine biome baseline with per-chunk hazard into a readable danger band. */
export function resolveDangerTier(biomeId: BiomeId, hazard: number): DangerTier {
  const base = BIOMES[biomeId].dangerTier;
  const nudge = hazard > 0.72 ? 1 : hazard < 0.32 ? -1 : 0;
  const tier = Math.min(3, Math.max(1, base + nudge));
  return tier as DangerTier;
}
